import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import {
  Badge,
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Skeleton,
  cn,
} from '@proerp/ui';

export interface KpiCardData {
  label: string;
  value: string;
  icon: LucideIcon;
  to: string;
  isLoading: boolean;
  /** Hebt kritische Kennzahlen hervor (nie nur über Farbe — Text bleibt explizit). */
  isAlert?: boolean;
  hint?: string;
  /** Optionaler Vergleichswert (z. B. ggü. Vorjahr); Pfeil + Text. */
  delta?: { text: string; positive: boolean } | null;
}

/**
 * KPI-Kacheln im Stil der shadcn dashboard-01 SectionCards: Beschreibung oben,
 * große tabellarische Zahl, rechts oben Alert-Badge oder Modul-Icon-Kachel,
 * Hinweis im Footer. Die ganze Kachel ist ein Link ins Modul (Overlay-Button).
 */
export function SectionCards({ kpis }: { kpis: KpiCardData[] }) {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {kpis.map((kpi) => (
        <Card
          key={kpi.label}
          className="relative transition-[box-shadow] has-[button:hover]:ring-foreground/20"
        >
          <button
            type="button"
            onClick={() => navigate(kpi.to)}
            aria-label={`${kpi.label} öffnen`}
            className="absolute inset-0 z-10 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <CardHeader className="relative pb-0">
            <CardDescription className={cn('truncate', kpi.isAlert ? 'pr-20' : 'pr-12')}>
              {kpi.label}
            </CardDescription>
            {kpi.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <CardTitle
                className={cn(
                  'truncate text-2xl font-semibold tabular-nums',
                  kpi.isAlert && 'text-danger-500',
                )}
              >
                {kpi.value}
              </CardTitle>
            )}
            <div className="absolute right-4 top-0">
              {kpi.isAlert && !kpi.isLoading ? (
                <Badge tone="danger">
                  <AlertTriangle aria-hidden />
                  prüfen
                </Badge>
              ) : (
                <span className="flex size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <kpi.icon className="size-4" strokeWidth={1.5} aria-hidden />
                </span>
              )}
            </div>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1 pt-4 text-sm text-muted-foreground">
            {kpi.delta && !kpi.isLoading && (
              <span
                className={cn(
                  'flex items-center gap-1 text-xs font-medium',
                  kpi.delta.positive ? 'text-success-500' : 'text-danger-500',
                )}
              >
                {kpi.delta.positive ? (
                  <TrendingUp className="size-3.5" strokeWidth={2} aria-hidden />
                ) : (
                  <TrendingDown className="size-3.5" strokeWidth={2} aria-hidden />
                )}
                {kpi.delta.text}
              </span>
            )}
            <span className="line-clamp-1">{kpi.hint ?? ' '}</span>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}
